/**
 * Variant siblings (collection/search/cards)
 *
 * Goal:
 * - Products remain separate in collections/search (backend reality)
 * - Clicking a product routes to a canonical "master" PDP, keeping URL unique
 * - Master PDP swaps displayed product via AJAX without changing URL
 *
 * Strategy (no metafields):
 * - Infer a "base title" shared prefix by querying /search/suggest.json for candidate prefixes
 * - Choose the base that yields the largest sibling set (>= 2). On ties, prefer the *shortest* base
 *   (this avoids accidentally absorbing color adjectives like "Dark" in "Dark Grey" when ambiguous).
 * - Master is deterministic: smallest handle among siblings.
 *
 * Safety:
 * - If inference is uncertain (no base yielding >= 2 siblings), do nothing.
 */

(() => {
  const normalize = (s) => (s || '').replace(/\s+/g, ' ').trim();
  const normLower = (s) => normalize(s).toLowerCase();
  const CONNECTOR_WORDS = new Set([
    'edizione',
    'limitata',
    'charm',
    'in',
    // common EN connectors that shouldn't start a "color"
    'limited',
    'edition',
    // materials/model words that should never start the "color" suffix
    'pelle',
    'liscia',
    'liscio',
    // model/material words that should never be treated as "color"
    'suede',
    'monogrammata',
    'monogrammato',
    'monogramm',
  ]);

  const productCardSelector = 'product-card[data-product-handle][data-product-title][data-product-url]';
  const processedAttr = 'data-variant-siblings-processed';

  function getStorefrontRoot() {
    const root = window.routes?.root_url || window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function canonicalHandle(handle) {
    return (handle || '').replace(/-en$/, '');
  }

  function imageKey(url) {
    if (!url) return '';
    return url.split('?')[0].replace(/^\/\//, '');
  }

  function deriveHandlePrefix(handle) {
    const canon = canonicalHandle(handle);
    const parts = canon.split('-');
    const inIdx = parts.indexOf('in');
    if (inIdx >= 0) return `${parts.slice(0, inIdx + 1).join('-')}-`;
    const lastDash = canon.lastIndexOf('-');
    return lastDash > 0 ? `${canon.slice(0, lastDash + 1)}` : `${canon}-`;
  }

  function localeCacheKey(query, root) {
    return `${root}|${normalize(query)}`;
  }

  function withItHandle(siblings) {
    return (siblings || []).map((s) => ({ ...s, itHandle: s.itHandle || s.handle }));
  }

  function resolveToItHandle(rawHandle, itSiblings, localeSiblings) {
    if (!rawHandle) return null;
    const canon = canonicalHandle(rawHandle);
    const itDirect = itSiblings.find(
      (s) => s.handle === rawHandle || canonicalHandle(s.handle) === canon
    );
    if (itDirect) return itDirect.handle;

    const localeMatch = localeSiblings?.find(
      (s) =>
        s.handle === rawHandle ||
        canonicalHandle(s.handle) === canon ||
        s.itHandle === rawHandle ||
        canonicalHandle(s.itHandle || '') === canon
    );
    if (localeMatch?.itHandle) return localeMatch.itHandle;
    return rawHandle;
  }

  function resolveToLocaleHandle(itHandle, localeSiblings) {
    if (!itHandle || !localeSiblings?.length) return itHandle;
    const canon = canonicalHandle(itHandle);
    const match = localeSiblings.find(
      (s) =>
        s.itHandle === itHandle ||
        s.handle === itHandle ||
        canonicalHandle(s.handle) === canon ||
        canonicalHandle(s.itHandle || '') === canon
    );
    return match?.handle || null;
  }

  function filterSiblingsForLocale(siblings, root) {
    const storefrontRoot = root || getStorefrontRoot();
    if (storefrontRoot === '/') {
      return siblings.filter((s) => {
        const path = (s.url || '').split('?')[0];
        return path.startsWith('/products/') && !path.startsWith('/en/');
      });
    }

    const filtered = siblings.filter((s) => {
      const path = (s.url || '').split('?')[0];
      return path.startsWith(`${storefrontRoot}products/`);
    });

    return filtered.length >= 2 ? filtered : siblings;
  }

  const suggestCache = new Map();

  async function fetchSearchAtRoot(query, root) {
    const q = normalize(query);
    if (!q) return [];
    const cacheKey = localeCacheKey(q, root);
    if (suggestCache.has(cacheKey)) return await suggestCache.get(cacheKey);

    const p = (async () => {
      const toItems = (raw) =>
        (raw || [])
          .map((r) => ({
            title: normalize(r.title),
            titleLower: normLower(r.title),
            url: r.url,
            image: r.image || r.featured_image || null,
            handle:
              r.handle ||
              (r.url ? r.url.split('?')[0].split('/').filter(Boolean).pop() : null),
          }))
          .filter((x) => x.handle && x.url && x.title);

      const fetchPage = async (page) => {
        const url =
          `${root}search?view=vs-siblings` +
          `&type=product` +
          `&options[prefix]=last` +
          `&options[unavailable_products]=show` +
          `&page=${page}` +
          `&q=${encodeURIComponent(q)}`;
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) return { pages: 1, items: [] };
        const txt = await res.text();
        let data = null;
        try {
          data = JSON.parse(txt);
        } catch {
          return { pages: 1, items: [] };
        }
        return { pages: Number(data?.pages || 1) || 1, items: toItems(data?.products) };
      };

      const first = await fetchPage(1);
      const map = new Map(first.items.map((it) => [it.handle, it]));
      const totalPages = Math.max(1, first.pages);
      const cap = Math.min(totalPages, 20);
      for (let page = 2; page <= cap; page++) {
        const next = await fetchPage(page);
        next.items.forEach((it) => map.set(it.handle, it));
      }
      return Array.from(map.values());
    })();

    suggestCache.set(cacheKey, p);
    return await p;
  }

  async function fetchSearchProducts(query) {
    return fetchSearchAtRoot(query, '/');
  }

  function isSameHandleFamily(handle, prefix, itSiblings) {
    const canon = canonicalHandle(handle);
    if (prefix && canon.startsWith(prefix)) return true;
    return (itSiblings || []).some((it) => canonicalHandle(it.handle) === canon);
  }

  async function localizeSiblingsFromItalian(itSiblings, base, targetRoot) {
    const root = targetRoot || getStorefrontRoot();
    const taggedIt = withItHandle(itSiblings);
    if (root === '/') return taggedIt;

    const localeProducts = await fetchSearchAtRoot(base, root);
    const prefix = deriveHandlePrefix(taggedIt[0]?.handle);
    const familyProducts = localeProducts.filter((p) =>
      isSameHandleFamily(p.handle, prefix, taggedIt)
    );

    const usedHandles = new Set();
    const mapped = [];

    for (const it of taggedIt) {
      const canon = canonicalHandle(it.handle);
      const imgKey = imageKey(it.image);
      let match = null;

      match = familyProducts.find(
        (p) => !usedHandles.has(p.handle) && canonicalHandle(p.handle) === canon
      );

      if (!match) {
        match = localeProducts.find(
          (p) => !usedHandles.has(p.handle) && canonicalHandle(p.handle) === canon
        );
      }

      if (!match && imgKey) {
        match = familyProducts.find(
          (p) => !usedHandles.has(p.handle) && imageKey(p.image) === imgKey
        );
      }

      if (!match && imgKey) {
        match = localeProducts.find(
          (p) => !usedHandles.has(p.handle) && imageKey(p.image) === imgKey
        );
      }

      if (match) {
        usedHandles.add(match.handle);
        mapped.push({ ...match, itHandle: it.handle });
      }
    }

    let result = filterSiblingsForLocale(mapped, root);
    return result.length >= 2 ? result : taggedIt;
  }

  async function buildSiblingGroup(base) {
    const itProducts = await fetchSearchAtRoot(base, '/');
    const itSiblings = siblingsForBase(itProducts, base);
    if (itSiblings.length < 2) return null;

    const currentRoot = getStorefrontRoot();
    const siblings =
      currentRoot === '/'
        ? withItHandle(itSiblings)
        : await localizeSiblingsFromItalian(itSiblings, base, currentRoot);

    return {
      base,
      itSiblings: withItHandle(itSiblings),
      siblings,
      masterHandle: pickMasterFromItalian(itSiblings, siblings),
    };
  }

  function candidatesFromTitle(title) {
    const words = normalize(title).split(' ').filter(Boolean);
    if (words.length < 2) return [];
    // Candidate bases: all prefixes except full title
    // We start from the *shorter* ones later for tie-breaking; here just list.
    const out = [];
    for (let i = 1; i <= words.length - 1; i++) {
      out.push(words.slice(0, i).join(' '));
    }
    return out;
  }

  function siblingsForBase(products, base) {
    const prefix = normLower(base) + ' ';
    if (prefix.trim().length < 2) return [];
    return products.filter((p) => {
      if (!p.titleLower.startsWith(prefix) || p.titleLower.length <= prefix.length) return false;
      return isValidBaseForTitle(p.title, base);
    });
  }

  function pickMasterHandle(siblings) {
    return siblings
      .map((s) => s.handle)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'en'))[0];
  }

  function pickMasterFromItalian(itSiblings, localeSiblings) {
    const itMaster = pickMasterHandle(withItHandle(itSiblings));
    return resolveToLocaleHandle(itMaster, localeSiblings) || itMaster;
  }

  function suffixInfoFor(title, baseWordCount) {
    const words = normalize(title).split(' ').filter(Boolean);
    const suffixWords = words.slice(baseWordCount);
    const first = (suffixWords[0] || '').toLowerCase();
    return {
      suffixWords,
      suffixLen: suffixWords.length,
      firstSuffixWord: first,
    };
  }

  function isValidBaseForTitle(title, base) {
    const baseWordCount = normalize(base).split(' ').filter(Boolean).length;
    const { suffixLen, firstSuffixWord } = suffixInfoFor(title, baseWordCount);
    if (suffixLen < 1 || suffixLen > 3) return false;
    if (!firstSuffixWord) return false;
    if (CONNECTOR_WORDS.has(firstSuffixWord)) return false;
    return true;
  }

  function groupContainsClickedProduct(siblings, title, handle, image) {
    if (!handle && !image) return true;

    const canon = canonicalHandle(handle || '');
    const imgKey = imageKey(image);
    const titleNorm = normLower(title);

    return siblings.some((s) => {
      if (imgKey && imageKey(s.image) === imgKey) return true;
      if (canon && canonicalHandle(s.handle) === canon) return true;
      if (canon && canonicalHandle(s.itHandle || '') === canon) return true;
      if (titleNorm && normLower(s.title) === titleNorm) return true;
      return false;
    });
  }

  function itSearchQueriesFromHandle(handle, localeTitle) {
    const canon = canonicalHandle(handle);
    const queries = [];

    if (canon.includes('piattina') && canon.includes('mini')) {
      queries.push('Piattina Mini in');
      queries.push('Mini Piattina in');
    }
    if (canon.includes('piattina') && (canon.includes('edizione') || canon.includes('limitata'))) {
      queries.push('Piattina Edizione Limitata in');
    } else if (canon.includes('piattina')) {
      queries.push('Piattina in');
    }
    if (canon.includes('amazzonia') && canon.includes('mini')) {
      queries.push('Mini Amazzonia in');
    } else if (canon.includes('amazzonia')) {
      queries.push('Amazzonia in');
    }

    const inMatch = normalize(localeTitle).match(/\bin\s+(.+)$/i);
    if (inMatch?.[1]) {
      const suffix = inMatch[1].trim();
      if (canon.includes('piattina') && canon.includes('mini')) {
        queries.push(`Piattina Mini in ${suffix}`);
      }
    }

    const lastSeg = canon.split('-').pop();
    if (lastSeg && lastSeg.length > 2) queries.push(lastSeg);

    return [...new Set(queries)];
  }

  async function resolveItalianTitleFromLocale(handle, localeTitle, image) {
    if (getStorefrontRoot() === '/') return normalize(localeTitle);

    const imgKey = imageKey(image);
    const canon = canonicalHandle(handle);
    const titleSuffix = normalize(localeTitle).match(/\bin\s+(.+)$/i)?.[1]?.trim();

    for (const q of itSearchQueriesFromHandle(handle, localeTitle)) {
      const products = await fetchSearchAtRoot(q, '/');
      if (!products.length) continue;

      if (imgKey) {
        const byImg = products.find((p) => imageKey(p.image) === imgKey);
        if (byImg) return byImg.title;
      }

      const byHandle = products.find((p) => canonicalHandle(p.handle) === canon);
      if (byHandle) return byHandle.title;

      // Avoid matching a bare color token against unrelated products (e.g. "glicine" → Hobo).
      if (titleSuffix && q.includes(' ')) {
        const suf = normLower(titleSuffix);
        const bySuffix = products.find((p) => {
          const tl = normLower(p.title);
          return tl.endsWith(` ${suf}`) || tl.endsWith(suf);
        });
        if (bySuffix) return bySuffix.title;
      }
    }

    return null;
  }

  async function inferGroupByTitlePrefix(title, handle, image) {
    const candidates = candidatesFromTitle(title);
    if (!candidates.length) return null;

    let best = null;

    for (const base of candidates) {
      if (!isValidBaseForTitle(title, base)) continue;

      const products = await fetchSearchProducts(base);
      const siblings = siblingsForBase(products, base);
      if (siblings.length < 2) continue;
      if (handle && !groupContainsClickedProduct(siblings, title, handle, image)) continue;

      const baseWordCount = normalize(base).split(' ').filter(Boolean).length;
      const suffixWordCounts = siblings.map((s) => {
        const info = suffixInfoFor(s.title, baseWordCount);
        return info.suffixLen;
      });
      const maxSuffixWords = Math.max(...suffixWordCounts);
      if (maxSuffixWords > 3) continue;

      const score = {
        count: siblings.length,
        baseWordCount,
        maxSuffixWords,
      };

      if (
        !best ||
        score.count > best.score.count ||
        (score.count === best.score.count && score.baseWordCount > best.score.baseWordCount) ||
        (score.count === best.score.count &&
          score.baseWordCount === best.score.baseWordCount &&
          score.maxSuffixWords < best.score.maxSuffixWords)
      ) {
        best = { base, siblings, score };
      }
    }

    if (!best) return null;
    const group = await buildSiblingGroup(best.base);
    if (!group) return null;
    return group;
  }

  async function inferGroupFromTitle(title, handle, image) {
    const direct = await inferGroupByTitlePrefix(title, handle, image);
    if (direct) return direct;

    if (!handle) return null;

    const itTitle = await resolveItalianTitleFromLocale(handle, title, image);
    if (!itTitle) return null;

    return await inferGroupByTitlePrefix(itTitle, handle, image);
  }

  function getCardImage(card) {
    return (
      card.querySelector('.media img, img[src*="cdn/shop"]')?.getAttribute('src') ||
      card.querySelector('img')?.getAttribute('src') ||
      null
    );
  }

  function rewriteCardLinks(card, masterHandle, base, clickedHandle, localeSiblings, itSiblings) {
    const activeIt = resolveToItHandle(clickedHandle, itSiblings, localeSiblings);
    const localeClicked = resolveToLocaleHandle(activeIt, localeSiblings) || clickedHandle;
    const masterUrl = `${getStorefrontRoot()}products/${encodeURIComponent(masterHandle)}`;
    const qs = new URLSearchParams();
    qs.set('base', base);
    qs.set('sibling', localeClicked);
    const href = `${masterUrl}?${qs.toString()}`;

    // Update all anchors inside card that point to the original product URL.
    // In this theme, image and title links are both <a href="{{ product.url }}">
    card.querySelectorAll('a[href]').forEach((a) => {
      const raw = a.getAttribute('href') || '';
      if (!raw.split('?')[0].includes('/products/')) return;
      a.setAttribute('href', href);
    });

    // Also update data-href used by the theme (collection-aware click handlers)
    card.querySelectorAll('[data-href]').forEach((el) => {
      el.setAttribute('data-href', href);
    });
  }

  async function processCardsOnce() {
    const cards = Array.from(document.querySelectorAll(productCardSelector)).filter(
      (c) => !c.hasAttribute(processedAttr)
    );
    if (!cards.length) return;

    // Only run on pages that render multiple products (collection/search/home lists).
    // If only 1 card exists, nothing to do.
    if (document.querySelectorAll(productCardSelector).length < 2) return;

    // Limit concurrency so we don't spam /search/suggest.json
    const concurrency = 4;
    let i = 0;
    const workers = new Array(concurrency).fill(0).map(async () => {
      while (i < cards.length) {
        const idx = i++;
        const card = cards[idx];
        card.setAttribute(processedAttr, 'true');

        const title = normalize(card.getAttribute('data-product-title'));
        const clickedHandle = card.getAttribute('data-product-handle');
        const image = getCardImage(card);
        if (!title || !clickedHandle) continue;

        const group = await inferGroupFromTitle(title, clickedHandle, image);
        if (!group) continue;

        rewriteCardLinks(
          card,
          group.masterHandle,
          group.base,
          clickedHandle,
          group.siblings,
          group.itSiblings
        );
      }
    });

    await Promise.all(workers);
  }

  // Run once on load, and also when infinite scroll injects more cards.
  const boot = () => {
    processCardsOnce().catch(() => {});
    const obs = new MutationObserver(() => processCardsOnce().catch(() => {}));
    obs.observe(document.documentElement, { subtree: true, childList: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();

